import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Button, ConfigProvider, Form, message } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { useNavigate } from 'react-router-dom';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import { CALENDLY_LINK } from 'constants/common';
import { PATH } from 'constants/path';
import { trackHeapEvent } from 'utils/analytics';
import {
    activateBillingOnboarding,
    getInsuranceBillingStatus,
    getStartedInsuranceBilling,
    saveOnboardingStep,
} from 'api/insuranceBilling';
import { clearProviderEmailCheckCache } from 'components/form/ProviderEmailInput';
import useEhrConnections from 'hooks/useEhrConnections';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import { REVIEW_META, STEPS } from './constants';
import { npiUnverified } from './utils';
import { BILLING_STEP_KEYS, renderBillingStep, type BillingStepKey } from './steps';
import {
    fieldMarkableErrors,
    getReviewErrors,
    getSectionErrors,
    getSectionMissingFields,
    REVIEW_SECTIONS,
} from './reviewSpec';
import ReviewSection from './components/ReviewSection';
import GrantAccessModal from './components/GrantAccessModal';
import {
    fetchOnboardingVo,
    hydrateManualTaxonomyOptions,
    hydratePaymentMethod,
    onboardingDtoToFormValues,
    STEP_API,
    STEP_FIELDS,
    stepValuesToDto,
} from './billingReviewData';
import s from './s.module.less';

const BannerIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" fill="currentColor" />
        <path d="M12 10v3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="16.8" r="1.1" fill="#fff" />
    </svg>
);

// 原型 CH_CALL_LINK：Keep current clearinghouse 时与 billing 团队约的 1:1
const CLEARINGHOUSE_CALL_LINK = 'https://calendly.com/d/cypr-jpq-h8w/1-1-with-klarity-success-team-tia';

// Review · Clearinghouse 卡下方的挂起说明（原型 .ibbw-rev-pending）
const ClearinghouseHold = () => (
    <div className={s.reviewHold}>
        <BannerIcon />
        <div>
            <strong>Submit is on hold until you have met with our billing team</strong>
            <br />
            Keeping your own clearinghouse limits what Klarity can see about your payments, so we walk through it
            with you first.{' '}
            <a href={CLEARINGHOUSE_CALL_LINK} target="_blank" rel="noopener noreferrer">
                Book a call with our billing team
            </a>
            , or choose Klarity&rsquo;s clearinghouse to submit now.
        </div>
    </div>
);

// Insurance billing onboarding 向导（原型 billing.html#onboarding）：
// 8 步填写 → Review（缺失拦截 + 内联编辑）→ Grant-access 弹窗 → activate → submitted 页。
// 中间步骤空必填不拦截 Continue，完整性只在 Review 页强制；
// 中途硬拦截：NPI 填了但未通过 Registry 校验（含查询中，见 npiUnverified）、
// rendering provider 的 NPI 是 Organization（NPI-2）、email 查重命中
export default function InsuranceBillingOnboarding() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    // activate 成功后刷新全局 status 缓存（Landing / dashboard gate 依赖 onboardingCompleted）
    const [, , refetchBillingStatus] = useInsuranceBillingStatus();
    const [currentStep, setCurrentStep] = useState(0);
    // 到达过的最远步骤：stepper 上允许点回已访问的步骤（原型 reached 语义）
    const [reached, setReached] = useState(0);
    const [reviewMode, setReviewMode] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [editingSections, setEditingSections] = useState<BillingStepKey[]>([]);
    const [savingSection, setSavingSection] = useState<BillingStepKey | null>(null);
    const [grantOpen, setGrantOpen] = useState(false);
    const [activating, setActivating] = useState(false);
    // Ops 是否已放行「保留自有 clearinghouse 也可提交」（GET /onboarding 的 billingSubmitAllowed）
    const [billingSubmitAllowed, setBillingSubmitAllowed] = useState(false);
    // payment 步骤 Continue 的门禁保存（tokenize + save）进行中
    const [paymentSaving, setPaymentSaving] = useState(false);
    // 续填判定 + hydrate 完成前不渲染表单，避免闪现空表单后跳步
    const [checkingResume, setCheckingResume] = useState(true);
    // custom EHR（isCustom，pending 等内部对接）没有可授权的账号，不进 GrantAccessModal 的授权名单；
    // useEhrConnections 全局同步，弹窗里登记/断开后这里自动跟上。
    // 绑定守卫放行（checkingResume 翻 false）前不拉取：未绑定 / 团队 provider 拉必 400 弹全局错误
    const [ehrConnections] = useEhrConnections(!checkingResume);
    const customEhrNames = new Set(ehrConnections.filter((c) => c.isCustom).map((c) => c.ehrSystem));
    // Cancel 内联编辑时用于回滚的整表快照
    const snapshotRef = useRef<Record<string, any> | null>(null);
    // setFields 标红后强制重算横幅 / 徽标
    const [, forceRefresh] = useState(0);
    // 提交暴露错误后已标红的字段集合（key = name path JSON，value = 错误文案 join）。
    // setFields 写入的错误是静态的（字段无 rules，antd 不会因输入而重校验），
    // 必须靠 syncReviewErrors 在值变化时对照此集合增删，否则修好了红字也不消失
    const flaggedReviewFieldsRef = useRef<Map<string, string>>(new Map());

    // 滚动容器是 fixed 的 wizardPage（不是 window）
    const wizardRef = useRef<HTMLDivElement>(null);
    const scrollTop = () => wizardRef.current?.scrollTo({ top: 0 });

    // billing 步骤的硬拦截：NPI 填了但未通过 Registry 校验（不足 10 位 / 查询中 / 查不到）时禁用 Continue。
    // useWatch 只作重渲染触发器（返回值会被已挂载子字段污染），值从原始 store 读取
    Form.useWatch(['billing'], form);
    const billingBlocked =
        BILLING_STEP_KEYS[currentStep] === 'billing-provider' &&
        !reviewMode &&
        npiUnverified(form.getFieldValue(['billing']));

    // rendering 步骤的硬拦截：编辑态 provider 的 NPI 未通过 Registry 校验、解析为 Organization（NPI-2），
    // 或 email 查重命中（emailStatus = duplicate，由 ProviderEmailInput blur 校验写入）时禁用 Continue
    Form.useWatch(['rendering', 'providers'], form);
    const renderingProviders: Record<string, any>[] = form.getFieldValue(['rendering', 'providers']) || [];
    const renderingBlocked =
        BILLING_STEP_KEYS[currentStep] === 'rendering-providers' &&
        !reviewMode &&
        renderingProviders.some((p, i) => {
            const same = i === 0 && p?.sameAsBilling === 'yes';
            const editable = i === 0 ? p?.sameAsBilling === 'no' : true;
            // same=yes 的 Provider 1 也有本行自己的 NPI 字段（预填/自填），NPI 拦截一并生效；email 字段仅编辑态有
            return (
                ((editable || same) && (npiUnverified(p) || p?.npiType === 'Type 2')) ||
                (editable && (p?.emailStatus === 'duplicate' || p?.emailStatus === 'unverifiable'))
            );
        });
    const continueBlocked = billingBlocked || renderingBlocked;

    // 挂载时 hydrate：拉聚合数据回填表单；completed 直接去 submitted 页；
    // 续填优先用服务端 onboardingStep（step key 即后端 slug），
    // 向导没有的 slug（ehr-connection）或无值时回落到第一个有必填缺失的步骤
    useEffect(() => {
        let cancelled = false;
        (async () => {
            // 绑定守卫：banner / 直链 / 浏览器后退等入口不经过 Landing 的 get-started，
            // 未绑定时向导内所有接口都 400。挂载先查 status，未 bound 且有资格发起（canBind）
            // 就补调幂等的 get-started，必须在下面的 hydrate 之前完成；
            // 失败不中断（全局 toast 已提示），团队 provider（canBind=false）无资格不调
            const statusRes: any = await getInsuranceBillingStatus();
            if (cancelled) return;
            const billingStatus = statusRes?.error ? null : statusRes?.data?.data;
            // 团队 provider（canBind=false）无资格发起绑定，向导对其必然全线 400：
            // 重定向到 dashboard 的 insurance-billing gate 页（按 canView 展示 Landing / dashboard）
            if (billingStatus && !billingStatus.canBind) {
                navigate(`${PATH.DASHBOARD}/${PATH.INSURANCE_BILLING}`, { replace: true });
                return;
            }
            if (billingStatus && billingStatus.bound !== true && billingStatus.canBind) {
                const startedRes: any = await getStartedInsuranceBilling();
                if (cancelled) return;
                if (!startedRes?.error) refetchBillingStatus();
            }
            // 已存卡（payment-methods）与聚合 VO 并行拉取；hydratePaymentMethod 直接写表单
            // payment.cardOnFile，须在缺失推算（fallbackIndex）之前完成，Step 8 才不会被误判未填
            const [vo] = await Promise.all([fetchOnboardingVo(), hydratePaymentMethod(form)]);
            if (cancelled) return;
            setBillingSubmitAllowed(!!vo?.billingSubmitAllowed);
            if (vo?.onboardingStatus === 'completed') {
                navigate(PATH.INSURANCE_BILLING_ONBOARDING_SUBMITTED, { replace: true });
                return;
            }
            const values = onboardingDtoToFormValues(vo);
            const hasData = Object.keys(values).length > 0;
            if (hasData) {
                form.setFieldsValue(values);
                // 直接续到 Review 时 Step 1 未挂载，手填 taxonomy 的 NPPES 列表要在这里补，否则误标 Differs
                await hydrateManualTaxonomyOptions(form);
                if (cancelled) return;
                const current = form.getFieldsValue(true);
                const firstIncomplete = BILLING_STEP_KEYS.findIndex(
                    (key) => getSectionMissingFields(current, key).length > 0,
                );
                const fallbackIndex = firstIncomplete === -1 ? BILLING_STEP_KEYS.length - 1 : firstIncomplete;
                // era save 后服务端指针自动前移到 payment-method，正好落在向导的 Step 8 上
                if (vo?.onboardingStep === 'review') {
                    setCurrentStep(BILLING_STEP_KEYS.length - 1);
                    setReached(BILLING_STEP_KEYS.length - 1);
                    setReviewMode(true);
                } else {
                    const serverIndex = BILLING_STEP_KEYS.indexOf(vo?.onboardingStep as BillingStepKey);
                    const resumeIndex = serverIndex >= 0 ? serverIndex : fallbackIndex;
                    setCurrentStep(resumeIndex);
                    // 缺失字段推算位之前的步骤视为已完成，stepper 允许点回
                    setReached(Math.max(resumeIndex, fallbackIndex));
                }
            }
            // 无数据字段的默认预选（step 4/5 same=yes、step 6 all patients + from today）。
            // 放在续填步骤推算之后，避免默认值把未填过的步骤算成已完成；
            // 只填空值，服务端已有的选择不覆盖。随所在步骤的 Continue/save 一起持久化
            if (form.getFieldValue(['location', 'sameAsBilling']) == null) {
                form.setFieldValue(['location', 'sameAsBilling'], 'yes');
            }
            const hydratedProviders: any[] = form.getFieldValue(['rendering', 'providers']) || [];
            if (hydratedProviders.length === 0) {
                form.setFieldValue(['rendering', 'providers'], [{ sameAsBilling: 'yes' }]);
            } else if (hydratedProviders[0]?.sameAsBilling == null) {
                form.setFieldValue(['rendering', 'providers', 0, 'sameAsBilling'], 'yes');
            }
            if (form.getFieldValue(['ehr', 'claimScope']) == null) {
                form.setFieldValue(['ehr', 'claimScope'], 'all');
            }
            if (form.getFieldValue(['ehr', 'claimStartMode']) == null) {
                form.setFieldValue(['ehr', 'claimStartMode'], 'today');
            }
            setCheckingResume(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Step 1 保存后端会按 NPI Registry 结果往 billingProvider 预填地址：
    // 每次进入 Step 3（address）重拉聚合数据回填，口径与挂载 hydrate 一致
    useEffect(() => {
        if (checkingResume || reviewMode || BILLING_STEP_KEYS[currentStep] !== 'address') return undefined;
        let cancelled = false;
        (async () => {
            const vo = await fetchOnboardingVo();
            if (cancelled) return;
            const serverAddress = onboardingDtoToFormValues(vo).address;
            if (serverAddress) form.setFieldsValue({ address: serverAddress });
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, reviewMode, checkingResume]);

    // 保存当前步骤（不阻塞导航；失败时全局错误 toast 已由 Request 层弹出）
    const saveStep = async (key: BillingStepKey): Promise<boolean> => {
        try {
            const body = stepValuesToDto(key, form.getFieldsValue(STEP_FIELDS[key]));
            const res: any = await STEP_API[key].save(body);
            return !res?.error;
        } catch (e) {
            return false;
        }
    };

    // 记录步骤指针（best-effort）：save 接口的响应自己向前推进指针，
    // PUT /step 只用于回退到已完成的步骤（向前 pin 会被后端 400 拒绝）
    const pinStep = async (slug: BillingStepKey | 'review') => {
        try {
            await saveOnboardingStep(slug);
        } catch (e) {
            // 忽略：指针仅影响下次续填定位
        }
    };

    const step = STEPS[currentStep];
    const isLast = currentStep === STEPS.length - 1;
    const values = reviewMode ? form.getFieldsValue(true) : {};
    const reviewErrors = reviewMode ? getReviewErrors(values) : [];
    const showBanner = submitAttempted && reviewErrors.length > 0;
    // 原型 V3：选 Keep current clearinghouse 时 Submit 挂起，直到 billing 团队与 provider 沟通后由 Ops 放行
    const clearinghousePending = reviewMode && values.clearinghouse?.choice === 'keep' && !billingSubmitAllowed;

    // 提交暴露错误后，值变化时把内联红字与实际错误对齐：修好的清除、新出现/变化的更新，
    // 并 forceRefresh 让 banner / Attention 徽标同步（顶层只订阅 billing/rendering，
    // 编辑其它 section 的字段不会自然触发重渲染）。无变化时不 setFields 不刷新，避免循环
    const syncReviewErrors = () => {
        if (!reviewMode || !submitAttempted) return;
        const vals = form.getFieldsValue(true);
        const current = new Map(
            fieldMarkableErrors(REVIEW_SECTIONS.flatMap((sec) => getSectionMissingFields(vals, sec.key))).map((f) => [
                JSON.stringify(f.name),
                f.errors.join('\n'),
            ]),
        );
        const prev = flaggedReviewFieldsRef.current;
        const updates: { name: NamePath; errors: string[] }[] = [];
        current.forEach((errors, key) => {
            if (prev.get(key) !== errors) updates.push({ name: JSON.parse(key), errors: errors.split('\n') });
        });
        prev.forEach((_, key) => {
            if (!current.has(key)) updates.push({ name: JSON.parse(key), errors: [] });
        });
        if (updates.length) {
            form.setFields(updates);
            flaggedReviewFieldsRef.current = current;
            forceRefresh((n) => n + 1);
        }
    };

    // NpiRegistryBox / ProviderEmailInput 用 setFieldValue 写辅助字段（npiType/npiChecking/emailStatus），
    // 不触发 onValuesChange；billing/rendering 的 useWatch 会带来重渲染，在渲染后补一次同步兜底
    useEffect(() => {
        syncReviewErrors();
    });

    const handleExit = async () => {
        trackHeapEvent('WebApp - Insurance Billing Onboarding - Click - Save And Exit', { step: currentStep });
        let saved = true;
        if (!reviewMode) {
            // 先存数据（会把服务端指针前推），再钉回用户实际所在位置。
            // payment 步骤没有可中途保存的草稿（卡号在 Square iframe 里，synced 卡本就无需上送），
            // 跳过 save，避免对半填的卡表单做 tokenize 弹错
            if (BILLING_STEP_KEYS[currentStep] !== 'payment-method') {
                saved = await saveStep(BILLING_STEP_KEYS[currentStep]);
            }
            await pinStep(BILLING_STEP_KEYS[currentStep]);
        } else {
            await pinStep('review');
        }
        // 保存失败时错误已由全局 toast 展示，不再弹成功文案
        if (saved) message.success('Your insurance billing onboarding has been saved.');
        navigate(-1);
    };

    const handleBack = () => {
        if (reviewMode) {
            setReviewMode(false);
            setEditingSections([]);
            pinStep(BILLING_STEP_KEYS[currentStep]);
            scrollTop();
            return;
        }
        if (currentStep > 0) {
            const prev = currentStep - 1;
            setCurrentStep(prev);
            pinStep(BILLING_STEP_KEYS[prev]);
            scrollTop();
            return;
        }
        navigate(-1);
    };

    const handleNext = () => {
        if (continueBlocked) return;
        trackHeapEvent('WebApp - Insurance Billing Onboarding - Click - Continue', {
            step: currentStep,
            key: step.key,
        });
        // payment 是唯一在本步就硬门禁的步骤（原型 stepBlocked 语义）：手动录卡要先过
        // Square tokenize + 保存，成功才放行进 Review；失败原因已内联/全局展示，停在本步
        if (BILLING_STEP_KEYS[currentStep] === 'payment-method') {
            if (paymentSaving) return;
            setPaymentSaving(true);
            (async () => {
                try {
                    const ok = await saveStep('payment-method');
                    if (!ok) return;
                    setReviewMode(true);
                    scrollTop();
                } finally {
                    setPaymentSaving(false);
                }
            })();
            return;
        }
        // 向前推进不调 PUT /step：save 成功后服务端指针自动前移
        saveStep(BILLING_STEP_KEYS[currentStep]);
        if (!isLast) {
            const next = currentStep + 1;
            setCurrentStep(next);
            setReached((r) => Math.max(r, next));
            scrollTop();
            return;
        }
        setReviewMode(true);
        scrollTop();
    };

    const goStep = (i: number) => {
        if (reviewMode || i === currentStep || i > reached) return;
        // 先存当前步数据，再记录跳转位置（仅回退时 pin；向前跳由 save 推进）。
        // payment 步骤同 Save and exit：无草稿可存，跳过 save
        const target = i;
        (async () => {
            if (BILLING_STEP_KEYS[currentStep] !== 'payment-method') {
                await saveStep(BILLING_STEP_KEYS[currentStep]);
            }
            if (target < currentStep) await pinStep(BILLING_STEP_KEYS[target]);
        })();
        setCurrentStep(i);
        scrollTop();
    };

    const openSectionEdit = (key: BillingStepKey) => {
        snapshotRef.current = form.getFieldsValue(true);
        setEditingSections((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };

    const cancelSectionEdit = (key: BillingStepKey) => {
        if (snapshotRef.current) form.setFieldsValue(snapshotRef.current);
        setEditingSections((prev) => prev.filter((k) => k !== key));
    };

    const saveSectionEdit = async (key: BillingStepKey) => {
        // 先按该 section 规则校验缺失/格式，未通过则标红定位不提交
        const current = form.getFieldsValue(true);
        const missing = getSectionMissingFields(current, key);
        if (missing.length > 0) {
            // 拦截按全部错误算；标红剔除 NpiRegistryBox 已内联展示的，避免重复红字
            form.setFields(fieldMarkableErrors(missing));
            form.scrollToField(missing[0].name, { behavior: 'smooth', block: 'center' });
            message.error('Please complete the required fields before saving.');
            return;
        }
        setSavingSection(key);
        try {
            const body = stepValuesToDto(key, form.getFieldsValue(STEP_FIELDS[key]));
            const res: any = await STEP_API[key].save(body);
            // 失败时错误已由全局 toast 展示，保持编辑态供用户修正
            if (res?.error) return;
            snapshotRef.current = form.getFieldsValue(true);
            setEditingSections((prev) => prev.filter((k) => k !== key));
            message.success('Changes saved.');
        } finally {
            setSavingSection((cur) => (cur === key ? null : cur));
        }
    };

    const handleActivate = async () => {
        setActivating(true);
        try {
            const res: any = await activateBillingOnboarding();
            if (res?.error) {
                // 错误文案由全局 toast 展示（后端 message 可直接给用户看），这里只处理副作用。
                // 业务错误码：91028 非 PPA / 91027 步骤必填缺失 / 91023 名单校验失败（一个账号都不建）/ 91026 并发锁 /
                // 91055 保留自有 clearinghouse 未获 Ops 放行
                const code = res?.errorCode;
                if (code === 91023 || code === 91027) {
                    // 关弹窗回 Review 修正；清 email 预检缓存——过期的「可用」判定可能正是失败原因
                    clearProviderEmailCheckCache();
                    setGrantOpen(false);
                } else if (code === 91028) {
                    setGrantOpen(false);
                } else if (code === 91055) {
                    // 选了 Keep current clearinghouse 但 Ops 尚未放行（如放行被撤回）：回 Review 显示挂起提示
                    setBillingSubmitAllowed(false);
                    setGrantOpen(false);
                }
                return;
            }
            trackHeapEvent('WebApp - Insurance Billing Onboarding - Activated', {});
            // 刷新全局 status 缓存（Landing / dashboard gate 读 onboardingCompleted）
            refetchBillingStatus();
            setGrantOpen(false);
            navigate(PATH.INSURANCE_BILLING_ONBOARDING_SUBMITTED);
        } catch (e) {
            message.error('Failed to activate insurance billing. Please try again.');
        } finally {
            setActivating(false);
        }
    };

    const handleReviewSubmit = () => {
        const current = form.getFieldsValue(true);
        const errors = getReviewErrors(current);

        if (errors.length) {
            setSubmitAttempted(true);
            // 展开所有有缺失的 section，滚动到第一个，并触发内联校验高亮
            const incomplete = REVIEW_SECTIONS.filter((sec) => getSectionErrors(current, sec.key).length).map(
                (sec) => sec.key,
            );
            snapshotRef.current = current;
            setEditingSections(incomplete);
            scrollTop();
            // 展开后字段才挂载，下一帧再标红缺失项并滚动到第一个 section
            setTimeout(() => {
                const fresh = form.getFieldsValue(true);
                const flagged = fieldMarkableErrors(incomplete.flatMap((key) => getSectionMissingFields(fresh, key)));
                form.setFields(flagged);
                // 记录标红集合，后续由 syncReviewErrors 随值变化增删
                flaggedReviewFieldsRef.current = new Map(
                    flagged.map((f) => [JSON.stringify(f.name), f.errors.join('\n')]),
                );
                forceRefresh((n) => n + 1);
                const first = document.querySelector(`[data-section="${incomplete[0]}"]`);
                if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 60);
            return;
        }
        trackHeapEvent('WebApp - Insurance Billing Onboarding - Submit', {});
        // 没有需要授权的外部 EHR（什么都没连 / 只有 custom / 只有 Klarity 自家 EHR）时跳过
        // grant 弹窗直接激活——否则弹窗一张卡都不渲染，Activate 永远点不了
        const externalEhrs: string[] = (current.ehr?.billingEhrs || []).filter(
            (n: string) => !customEhrNames.has(n) && !n.includes('Klarity'),
        );
        if (externalEhrs.length === 0) {
            handleActivate();
            return;
        }
        setGrantOpen(true);
    };

    // Grant modal 打开时机在 review 提交后，步骤字段多已卸载：直接读完整 store（打开时点已定，无需订阅）。
    // custom EHR（isCustom）没有可授权的账号、Klarity 自家 EHR 平台本就有访问权限，都不进授权名单
    const fullValues = form.getFieldsValue(true) || {};
    const grantEhrs: string[] = (fullValues.ehr?.billingEhrs || []).filter(
        (n: string) => !customEhrNames.has(n) && !n.includes('Klarity'),
    );
    const grantOrganizationName: string | undefined = fullValues.billing?.organizationName;

    return (
        <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
            <div className={s.wizardPage} ref={wizardRef}>
                <div className={s.topbar}>
                    <PPAOnboardingHeader showLogout webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR} />
                </div>

                <div className={s.main}>
                    <div className={s.mainCol}>
                        {!checkingResume && reviewMode && (
                            <>
                                <div className={s.wizHeader}>
                                    <div className={s.wizTitle}>{REVIEW_META.title}</div>
                                    <div className={s.wizSubtitle}>{REVIEW_META.sub}</div>
                                </div>
                                <Form
                                    form={form}
                                    layout="vertical"
                                    requiredMark={false}
                                    className={s.wizBody}
                                    onValuesChange={syncReviewErrors}
                                >
                                    {showBanner && (
                                        <div className={s.reviewErrorBanner}>
                                            <BannerIcon />
                                            <div>
                                                <div className={s.reviewErrorTitle}>
                                                    Please complete the following before submitting
                                                </div>
                                                <ul>
                                                    {reviewErrors.map((e) => (
                                                        <li key={e}>{e}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                    {REVIEW_SECTIONS.map((sec) => (
                                        <ReviewSection
                                            key={sec.key}
                                            sectionKey={sec.key}
                                            title={sec.title}
                                            values={values}
                                            editing={editingSections.includes(sec.key)}
                                            saving={savingSection === sec.key}
                                            hasErrors={submitAttempted && getSectionErrors(values, sec.key).length > 0}
                                            onEdit={() => openSectionEdit(sec.key)}
                                            onCancel={() => cancelSectionEdit(sec.key)}
                                            onSave={() => saveSectionEdit(sec.key)}
                                            viewOpts={{ review: true, clearinghousePending }}
                                            readExtra={
                                                sec.key === 'era' && clearinghousePending ? <ClearinghouseHold /> : undefined
                                            }
                                        >
                                            {renderBillingStep(sec.key)}
                                        </ReviewSection>
                                    ))}
                                </Form>
                            </>
                        )}

                        {!checkingResume && !reviewMode && (
                            <>
                                <div className={s.stepsNav}>
                                    {STEPS.map((st, i) => (
                                        <div
                                            key={st.key}
                                            className={cx(
                                                s.stepItem,
                                                i === currentStep && s.active,
                                                // 进度高亮只到当前步（与 credentialing 一致）；Back 后随当前步回退。
                                                // reached 仍控制可点击范围（到达过的步骤可直接点回）
                                                i < currentStep && s.done,
                                            )}
                                            role="button"
                                            tabIndex={i <= reached ? 0 : -1}
                                            onClick={() => goStep(i)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    goStep(i);
                                                }
                                            }}
                                        >
                                            <div className={s.stepBar} />
                                            <div className={s.stepNum}>Step {i + 1}</div>
                                            <div className={s.stepName}>{st.label}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className={s.wizHeader}>
                                    <div className={s.wizTitle}>{step.title}</div>
                                    <div className={s.wizSubtitle}>{step.sub}</div>
                                </div>

                                <Form
                                    form={form}
                                    layout="vertical"
                                    requiredMark={false}
                                    className={s.wizBody}
                                    scrollToFirstError
                                >
                                    {renderBillingStep(BILLING_STEP_KEYS[currentStep])}
                                </Form>
                            </>
                        )}
                    </div>
                </div>

                <div className={s.footer}>
                    <div className={s.footerInner}>
                        <Button size="large" className={s.exitBtn} onClick={handleExit}>
                            Save and exit
                        </Button>
                        <div className={s.footerRight}>
                            <Button type="link" size="large" className={s.backBtn} onClick={handleBack}>
                                Back
                            </Button>
                            {reviewMode ? (
                                <Button
                                    type="primary"
                                    size="large"
                                    className={s.nextBtn}
                                    onClick={handleReviewSubmit}
                                    // 有 section 正在内联编辑时禁止提交（须先 Save changes / Cancel）；
                                    // 保留自有 clearinghouse 且未获放行时同样禁止
                                    disabled={editingSections.length > 0 || clearinghousePending}
                                    // 跳过 grant 弹窗直接激活的路径（只勾了 Klarity EHR）在本按钮上给 loading 反馈
                                    loading={activating}
                                >
                                    Submit
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    size="large"
                                    className={s.nextBtn}
                                    onClick={handleNext}
                                    disabled={continueBlocked}
                                    // payment 步骤门禁保存（tokenize + save）期间给 loading 反馈
                                    loading={paymentSaving}
                                >
                                    Continue
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <GrantAccessModal
                    open={grantOpen}
                    ehrs={grantEhrs}
                    organizationName={grantOrganizationName}
                    activating={activating}
                    onBack={() => setGrantOpen(false)}
                    onActivate={handleActivate}
                />
            </div>
        </ConfigProvider>
    );
}
